import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

type IndustryItem = { title: string; description: string };
type IndustrySolution = { title: string; bullets: string[] };
type IndustryPhase = { title: string; steps: IndustryItem[] };

const solutionIcons = ["⚡", "🛡", "⚙", "📊"];
const solutionAccentColors = [
  { bg: "from-amber-400 to-orange-500", text: "text-amber-500", light: "bg-amber-50" },
  { bg: "from-cyan-400 to-blue-500", text: "text-cyan-500", light: "bg-cyan-50" },
  { bg: "from-violet-400 to-purple-500", text: "text-violet-500", light: "bg-violet-50" },
  { bg: "from-emerald-400 to-teal-500", text: "text-emerald-500", light: "bg-emerald-50" },
];

export type IndustrySolutionSectionImages = {
  /** 痛点区左侧配图 */
  painPoints: string;
  /** 方案区左侧配图 */
  solutions: string;
  /** 底部 CTA 背景图 */
  cta: string;
};

export type IndustrySolutionSectionsProps = {
  /** i18n namespace segment, e.g. `furniture` → `furniture.painPoints.title` */
  i18nPrefix: string;
  images: IndustrySolutionSectionImages;
  imagesPos?: {
    painPoints?: string;
    solutions?: string;
    cta?: string;
  };
};

export default function IndustrySolutionSections({ i18nPrefix, images, imagesPos }: IndustrySolutionSectionsProps) {
  const { t } = useTranslation();

  const painPoints = useMemo(
    () => t(`${i18nPrefix}.painPoints.items`, { returnObjects: true }) as IndustryItem[],
    [t, i18nPrefix]
  );
  const solutions = useMemo(
    () => t(`${i18nPrefix}.solutions.items`, { returnObjects: true }) as IndustrySolution[],
    [t, i18nPrefix]
  );
  const phases = useMemo(
    () => t(`${i18nPrefix}.serviceFlow.phases`, { returnObjects: true }) as IndustryPhase[],
    [t, i18nPrefix]
  );

  return (
    <>
      <section className="relative w-full bg-white py-[60px]">
        <div className="w-1200 px-4">
          <h2 className="pb-5 text-center text-[34px] font-bold text-[#222]">
            {t(`${i18nPrefix}.painPoints.title`)}
          </h2>
          <p className="text-center text-[18px] text-[#666]">{t(`${i18nPrefix}.painPoints.subtitle`)}</p>
          <div className="mt-12 grid items-stretch gap-8 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <div
                className="relative min-h-[320px] overflow-hidden rounded-2xl md:min-h-[420px] lg:h-full"
                data-aos="fade-up"
              >
                <img
                  src={images.painPoints}
                  alt=""
                  className="h-full w-full object-cover"
                  style={{ objectPosition: imagesPos?.painPoints || "50% 50%" } as React.CSSProperties}
                />
                <div className="absolute inset-x-4 bottom-4 rounded-xl bg-white/90 p-4 shadow-lg backdrop-blur-sm">
                  <h3 className="text-lg font-semibold text-[#222]">
                    {t(`${i18nPrefix}.painPointsImage.title`)}
                  </h3>
                  <p className="mt-1 text-[14px] leading-6 text-[#666]">
                    {t(`${i18nPrefix}.painPointsImage.description`)}
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:col-span-7">
              {painPoints.map((item, index) => (
                <div key={item.title} data-aos="fade-up" data-aos-delay={index * 100} className="h-full">
                  <article className="group relative rounded-2xl border border-slate-100 bg-white p-8 shadow-sm h-full transition-all duration-300 hover:border-blue-100 hover:shadow-lg">
                  <div className="absolute top-0 left-0 h-1 w-full rounded-t-2xl bg-gradient-to-r from-blue-400 to-cyan-400 opacity-0 transition-opacity group-hover:opacity-100" />
                  <div className="flex items-start gap-4">
                    <div className="-ml-2 flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 text-blue-500 transition-transform duration-300 group-hover:scale-110">
                      <span className="text-xl font-bold">{String(index + 1).padStart(2, "0")}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900 transition-colors group-hover:text-blue-800">
                        {item.title}
                      </h3>
                      <p className="mt-3 text-[15px] leading-7 text-slate-500">{item.description}</p>
                    </div>
                  </div>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative w-full bg-[#f2f4f6] py-[60px]">
        <div className="w-1200 px-4">
          <h2 className="pb-5 text-center text-[34px] font-bold text-[#222]">
            {t(`${i18nPrefix}.solutions.title`)}
          </h2>
          <p className="text-center text-[18px] text-[#666]">{t(`${i18nPrefix}.solutions.subtitle`)}</p>
          <div className="mt-12 grid items-stretch gap-8 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <div
                className="relative min-h-[360px] overflow-hidden rounded-2xl md:min-h-[440px] lg:h-full lg:min-h-0"
                data-aos="fade-up"
              >
                <img
                  src={images.solutions}
                  alt=""
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  style={{ objectPosition: imagesPos?.solutions || "50% 50%" } as React.CSSProperties}
                />
                <div className="absolute bottom-4 left-4 rounded-lg bg-white/90 px-4 py-2 shadow-lg backdrop-blur-sm">
                  <span className="text-sm font-semibold text-[#222]">
                    {t(`${i18nPrefix}.solutionsImage.tag`)}
                  </span>
                </div>
              </div>
            </div>
            <div className="space-y-8 lg:col-span-7">
              {solutions.map((block, index) => {
                const accent = solutionAccentColors[index % solutionAccentColors.length];
                return (
                  <div key={block.title} data-aos="fade-up" data-aos-delay={index * 100}>
                  <article className="group relative overflow-hidden rounded-3xl border border-slate-200/60 bg-white shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-md">
                    <div className="absolute -top-3 -left-3 z-10">
                      <div
                        className={`relative flex size-16 items-center justify-center rounded-br-3xl bg-gradient-to-br ${accent.bg} shadow-lg`}
                      >
                        <div className="absolute inset-0 top-3 left-3 rounded-full border border-white/30" />
                        <span className="relative pt-2 pl-2 text-2xl font-black text-white drop-shadow-md">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <div className="absolute top-2.5 right-1">
                          <span className="text-xs text-zinc-100/80">✦</span>
                        </div>
                      </div>
                    </div>

                    <div className="absolute top-6 right-6 hidden">
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-2xl ${accent.light} ${accent.text} transition-transform duration-300 group-hover:rotate-3 group-hover:scale-110`}
                      >
                        {solutionIcons[index % solutionIcons.length]}
                      </div>
                    </div>

                    <div className="p-8 pt-4 md:pt-6">
                      <h3 className="ml-9.5 mb-6 text-2xl leading-snug font-bold text-slate-900 md:text-[26px] ">
                        {block.title}
                      </h3>

                      <ul className="space-y-3">
                        {block.bullets.map((point) => (
                          <li key={point} className="flex items-start gap-4">
                            <div
                              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${accent.bg}`}
                            >
                              <span className="text-[11px] leading-none font-bold text-white">✓</span>
                            </div>
                            <span className="text-[15px] leading-relaxed text-slate-600">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className={`h-1.5 w-full bg-gradient-to-r ${accent.bg} opacity-60`} />
                    <div
                      className={`absolute -right-20 -bottom-20 h-40 w-40 rounded-full bg-gradient-to-br ${accent.bg} opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-20`}
                    />
                  </article>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="relative w-full bg-white py-[60px]">
        <div className="w-1200 px-4">
          <h2 className="pb-5 text-center text-[34px] font-bold text-[#222]">
            {t(`${i18nPrefix}.serviceFlow.title`)}
          </h2>
          <p className="text-center text-[18px] text-[#666]">{t(`${i18nPrefix}.serviceFlow.subtitle`)}</p>
          <div className="mt-10 space-y-8">
            {phases.map((phase, phaseIndex) => (
              <div
                key={phase.title}
                className="rounded-2xl border border-[#e8ebee] bg-white p-6 shadow-sm"
                data-aos="fade-up"
                data-aos-delay={phaseIndex * 80}
              >
                <h3 className="text-[26px] font-semibold text-[#222]">{phase.title}</h3>
                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  {phase.steps.map((step) => (
                    <article key={step.title} className="rounded-xl bg-[#f5f7f9] p-4">
                      <h4 className="font-semibold text-[#222]">{step.title}</h4>
                      <p className="mt-2 text-[15px] leading-7 text-[#666]">{step.description}</p>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative w-full overflow-hidden bg-[#0f172a]">
        <div className="relative isolate min-h-[300px] w-full md:min-h-[360px]">
          <img
            src={images.cta}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-[50%_12%] opacity-45 md:opacity-55"
            style={{ objectPosition: imagesPos?.cta || "50% 12%" } as React.CSSProperties}
            data-aos="fade-up"
            data-aos-duration="700"
          />
          <div
            className="absolute inset-0 bg-linear-to-r from-[#0f172a]/95 via-[#0f172a]/55 to-transparent"
            aria-hidden
          />
          <div className="relative z-10 w-1200 px-4 py-16 md:px-8 md:py-20">
            <div className="max-w-[560px]">
              <h2
                className="text-3xl font-bold text-white md:text-4xl"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                {t(`${i18nPrefix}.cta.title`)}
              </h2>
              <p
                className="mt-4 leading-7 text-white/80"
                data-aos="fade-up"
                data-aos-delay="200"
              >
                {t(`${i18nPrefix}.cta.subtitle`)}
              </p>
              <div data-aos="fade-up" data-aos-delay="300">
                <Link
                  to="/schemeCustomization"
                  className="mt-8 inline-flex items-center rounded-full bg-[#0f5f95] px-8 py-3.5 font-semibold text-white transition-colors hover:bg-[#0b4a76]"
                >
                  {t(`${i18nPrefix}.cta.button`)}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
